import React, { useEffect, useRef } from 'react';
import { LOOTBOX_RARITIES, getS2ItemValue } from '../../constants/lootbox';
import { VFX_EFFECTS } from '../../constants/vfx';
import { VfxSpan, fmtTokens } from '../../utils/helpers';
import ItemIcon from './ItemIcon';

function LootboxRewardModal({ item, onClose, inventory, onSellDuplicate, onQuickSell, isS2 }) {
  const rarity = LOOTBOX_RARITIES[item.rarity];
  const quickSellPrice = item.rarity === 'COMMON' ? 0.5 : 1;
  const isShinyGalaxy = item.isShiny && item.rarity === 'GALAXY';
  const isNuclear = item.rarity === 'NUCLEAR';
  const sirenRefs = useRef([]);

  // Play air raid siren 3 times in a row for nuclear items
  useEffect(() => {
    if (!isNuclear) return;

    let playCount = 0;

    function playNext() {
      if (playCount >= 3) return;
      const siren = new Audio('/AirraidSiren.mp3');
      siren.volume = 1.0;
      sirenRefs.current.push(siren);
      siren.addEventListener('ended', () => {
        playCount++;
        playNext();
      });
      siren.play().catch(err => console.log('Siren play failed:', err));
    }

    playNext();

    return () => {
      sirenRefs.current.forEach(s => { s.pause(); s.currentTime = 0; });
      sirenRefs.current = [];
    };
  }, [isNuclear]);

  // Check if this item is a duplicate (exact match in inventory before this was added)
  // VFX, Shiny, and StatTrak variants are considered distinct items
  const isDuplicate = React.useMemo(() => {
    if (!inventory || inventory.length === 0) return false;

    // Create key for current item — include all variant modifiers
    const itemKey = (i) => `${i.id}_${i.rarity}_${i.isStatTrak || false}_${i.isShiny || false}_${i.vfx || 'none'}`;
    const currentKey = itemKey(item);

    // Count how many times this exact variant appears in inventory
    const count = inventory.filter(invItem => itemKey(invItem) === currentKey).length;

    // It's a duplicate if we have more than 1 (since we just added one)
    return count > 1;
  }, [inventory, item]);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content lootbox-reward-modal"
        onClick={(e) => e.stopPropagation()}
        style={{ borderColor: isNuclear || isShinyGalaxy ? 'transparent' : item.isShiny ? '#FFD700' : (item.isStatTrak ? '#FF8C00' : rarity.color) }}
      >
        <button className="modal-close" onClick={onClose}>✕</button>

        <div className="reward-content">
          <div className="reward-header">
            <h2 style={{ color: item.isStatTrak ? '#FF8C00' : rarity.color }}>
              {isNuclear ? '☢️ NUCLEAR MELTDOWN! ☢️' :
               isShinyGalaxy ? '🌌 GALAXY SHINY! 🌌' :
               item.isStatTrak && item.isShiny ? '🔥 SHINY STATTRAK! 🔥' :
               item.isStatTrak ? '📊 STATTRAK™! 📊' :
               item.isShiny ? '✨ SHINY! ✨' :
               '🎉 You Got!'}
            </h2>
            {isDuplicate && (
              <p style={{
                color: '#FFA500',
                fontSize: '14px',
                marginTop: '8px',
                fontWeight: 'bold'
              }}>
                ⚠️ You already own this item!
              </p>
            )}
          </div>

          <div
            className={`reward-item ${rarity.glow ? 'glow' : ''} ${isNuclear ? 'nuclear' : isShinyGalaxy ? 'shiny-galaxy' : item.isShiny ? 'shiny' : ''} ${item.isStatTrak ? 'stattrak' : ''}`}
            style={{
              borderColor: isNuclear || isShinyGalaxy ? 'transparent' : item.isShiny ? '#FFD700' : (item.isStatTrak ? '#FF8C00' : rarity.color),
              boxShadow: isNuclear || isShinyGalaxy
                ? '' // Handled by nuclear or shiny-galaxy animation
                : item.isShiny
                ? '0 0 40px #FFD700, 0 0 20px #FFF'
                : item.isStatTrak
                ? '0 0 40px #FF8C00, 0 0 20px #FF6B35'
                : (rarity.glow ? `0 0 30px ${rarity.color}` : 'none')
            }}
          >
            {isNuclear ? (
              <div className="nuclear-badge">☢️ NUCLEAR ☢️</div>
            ) : isShinyGalaxy ? (
              <div className="shiny-galaxy-badge">🌌 GALAXY ✨</div>
            ) : item.isShiny && (
              <div className="shiny-badge">✨ SHINY ✨</div>
            )}
            {item.isStatTrak && (
              <div className="stattrak-badge" style={{
                position: 'absolute',
                top: item.isShiny ? '40px' : '8px',
                right: '8px',
                background: 'linear-gradient(135deg, #FF8C00, #FF6B35)',
                padding: '4px 8px',
                borderRadius: '4px',
                fontWeight: 'bold',
                fontSize: '12px',
                color: '#000',
                boxShadow: '0 0 10px #FF8C00'
              }}>
                STATTRAK™
              </div>
            )}
            <div className="reward-icon">
              {item.vfx
                ? <VfxSpan vfx={item.vfx} style={{ display: 'inline-block', fontSize: 'inherit' }}><ItemIcon item={item} size={80} /></VfxSpan>
                : <ItemIcon item={item} size={80} />}
            </div>
            <div className="reward-name" style={{ color: item.isShiny ? '#FFD700' : rarity.color }}>
              {item.vfx
                ? <VfxSpan vfx={item.vfx}>{item.name}</VfxSpan>
                : item.name}
              {item.isStatTrak && <span style={{ marginLeft: '4px', fontSize: '16px', color: '#FF8C00' }}>™</span>}
              {item.vfx && item.vfx.length > 0 && <span style={{ marginLeft: '6px', fontSize: '13px', color: '#a78bfa', fontWeight: 'bold' }}>✦ VFX{Array.isArray(item.vfx) && item.vfx.length > 1 ? ` x${item.vfx.length}` : ''}</span>}
            </div>
            <div className="reward-rarity" style={{ color: rarity.color }}>
              {rarity.name}
            </div>
            {isS2 && (
              <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#4CAF50', marginTop: '4px' }}>
                Worth ${fmtTokens(getS2ItemValue(item))}
              </div>
            )}
            <div className="reward-description">{item.description}</div>
            {item.vfx && item.vfx.length > 0 && (
              <div style={{ marginTop: '12px', padding: '10px 14px', background: 'rgba(167,139,250,0.1)', border: '1px solid rgba(167,139,250,0.4)', borderRadius: '8px' }}>
                <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#a78bfa', marginBottom: '6px' }}>✦ VFX Effects</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', justifyContent: 'center' }}>
                  {(Array.isArray(item.vfx) ? item.vfx : [item.vfx]).map((effectId, idx) => {
                    const effect = VFX_EFFECTS.find(e => e.id === effectId);
                    return (
                      <div key={idx} style={{ padding: '3px 8px', background: 'rgba(167,139,250,0.2)', border: '1px solid #a78bfa', borderRadius: '4px', fontSize: '11px', color: '#a78bfa' }}>
                        {effect ? effect.name : effectId}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {item.isStatTrak && item.trackedStats && (
              <div className="stattrak-stats-reveal" style={{
                marginTop: '16px',
                padding: '12px',
                background: 'rgba(255, 140, 0, 0.1)',
                border: '1px solid #FF8C00',
                borderRadius: '8px'
              }}>
                <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#FF8C00', marginBottom: '8px' }}>
                  📊 Tracking Stats:
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', justifyContent: 'center' }}>
                  {item.trackedStats.map((stat, idx) => (
                    <div key={idx} style={{
                      padding: '4px 8px',
                      background: 'rgba(255, 140, 0, 0.2)',
                      border: '1px solid #FF8C00',
                      borderRadius: '4px',
                      fontSize: '12px',
                      color: '#FF8C00'
                    }}>
                      {stat}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: '12px', width: '100%' }}>
            {isDuplicate && onSellDuplicate && (
              <button
                className="btn-primary"
                onClick={() => onSellDuplicate(item)}
                style={{
                  flex: 1,
                  background: 'linear-gradient(135deg, #FF8C00, #FF6B35)',
                  border: '2px solid #FF8C00'
                }}
              >
                📋 List on Market
              </button>
            )}
            <button
              className="btn-primary"
              onClick={onClose}
              style={{ flex: 1 }}
            >
              Collect Reward
            </button>
            {isDuplicate && onQuickSell && (
              <button
                className="btn-primary"
                onClick={() => onQuickSell(item)}
                style={{
                  flex: 1,
                  background: 'linear-gradient(135deg, #228B22, #006400)',
                  border: '2px solid #32CD32'
                }}
              >
                💸 Sell for {quickSellPrice} 🔵
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default LootboxRewardModal;
